import { ActionPermission, Api, DateUtil, DisplayType, LogManager, McLeodClassicIntegration, Model, ModelRow, ModelRowType, ObjectUtil, PermissionsUtil } from "@mcleod/core";
import { ClickEvent, ContextMenuCreationEvent, DataHeader, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, Layout, LayoutProvidedCrudDecoratorProps, SaveAction, SaveButton, Snackbar } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { OrderTabView } from "@mcleod/dispatch";
import { RetransmitStatusModal } from "./RetransmitStatusModal";
import { ConfirmOrderSave } from "./ConfirmOrderSave";
import { RetransmitStatusModalOrderSave } from "./RetransmitStatusModalOrderSave";
import { EdiRawDataSlideout } from "./EdiRawDataSlideout";
import { AutogenLayoutViewEdiStatus } from "./autogen/AutogenLayoutViewEdiStatus";
import { LMEUserSettings } from "@mcleod/general";

const log = LogManager.getLogger("lme.datafusion.ViewEdiStatus");

export class ViewEdiStatus extends AutogenLayoutViewEdiStatus {

    private _doAfterAction: () => void = () => this.mainDataSource.search({ id: this.mainDataSource.activeRow.get("id") });
    private _viewOrderURL = "lme/dispatch/OrderTabView?mode=update&orderId=";

    /**
     * Maps mainDataSource to the primary data source for this layout
     */
    public get mainDataSource() {
        return this.sourceEdistatus;
    }

    public set mainDataSource(value) {
        // The framework may try to set this property, but we always want it to point to sourceEdistatus
        // So we can either ignore the setter or log a warning
        console.warn("Attempted to set mainDataSource, but it should always point to sourceEdistatus");
    }
    private _createdPostListener = false;
    private fieldList: string[] = ["blnum", "commodity_id", "commodity", "equipment_type_id", "hazmat_code_id", "collection_method", "order_cube", "order_pieces", "order_weight", "order_weight_um",
        "ordered_date", "seal", "shipment_id", "refno", "cons_stop_id", "ship_stop_id", "cc_pro_nbr", "appointment_date", "status_date", "frt_grp_pro_nbr", "pro_nbr", "delay_code",
        "curr_stop_id", "curr_latitude", "curr_longitude", "movement_id", "movement_sequence", "actual_arrival", "actual_departure", "eta", "sched_arrive_early", "sched_arrive_late",
        "curr_volume", "curr_volume_um", "curr_weight", "curr_weight_um", "curr_stop_type", "customer_id", "scac", "shipment_id", "edi_commodity_id", "edi_equip_type_id", "bill_plant",
        "manifest_id", "carrier_trailer", "curr_plant", "shipper_plant", "consignee_plant", "prev_plant", "next_plant", "timezone_id", "service_standard", "curr_city_name", "curr_state", "curr_zip_code",
        "curr_city_id", "curr_address", "curr_loc_id", "curr_loc_name", "curr_contact_name", "curr_phone", "trip_lease", "delivery_signature", "curr_cases"];
    private _disableSaveButton = false;
    private _usedResetStatus = false;
    private _saveBackToOrder = false;
    private _saveDataToOrder = false;
    private _changedData: Partial<any>;
    private _postSaveValidationPromise: Promise<void> = Promise.resolve();

    /**
     * Returns a promise that resolves when any post-save validation is complete.
     * Callers can await this to ensure validation has finished before refreshing data.
     */
    public get postSaveValidationComplete(): Promise<void> {
        return this._postSaveValidationPromise;
    }

    override onLoad(): void {
        this.statusdataheader["labelTitle"].visible = false;
        this.tabFuncAck.visible = false;
        this._disableSaveButton = true;
        this.labelOrderStatus.visible = false;
        this.imageGoToOrder.visible = false;
        this.classicButton.visible = false;
        this.setSearchSpecificProperties();

        // Add global access for debugging
        (window as any).viewEdiStatus = this;
    }

    async testAwait(): Promise<any> {
        return await CommonDialogs.showYesNo("Are you sure you want to close this screen?");
    }

    /** This is an event handler for the onContextMenuCreation event of statusdataheader. */
    statusdataheaderOnContextMenuCreation(event: ContextMenuCreationEvent) {
        if (this.activeRow == null) {
            event.preventDefault();
            return;
        }

        const direction = this.activeRow?.get("direction");
        const ediStatusRow = this.activeRow;
        const readyToSend = ediStatusRow?.get("ready_to_send");
        const templateType = ediStatusRow?.get("edi_template_format_type");

        event.contextMenu.updateMenuLabels({
            void: {
                visible: direction === "O" && readyToSend !== "H" && readyToSend !== "V",
                onClick: () => this.voidStatus(ediStatusRow)
            },
            restore: {
                visible: direction === "O" && readyToSend === "V",
                onClick: () => this.restoreStatus(ediStatusRow)
            },
            resetStatus: {
                visible: direction === "O" && readyToSend !== "V",
                onClick: () => this.resetStatus(ediStatusRow)
            },
            ediToEnglish: {
                visible: templateType !== "X" && templateType !== "J",
                onClick: () => this.openRawDataSlideout(false)
            },
            rawData: {
                onClick: () => this.openRawDataSlideout(true)
            }
        });
    }

    private async resetStatus(ediStatus: ModelRow<any>) {
        const statusId = ediStatus.getString("id");

        if (statusId) {
            return await Api.search("lme/datafusion/reset-status-data", {
                status_id: statusId
            }).then(async (response) => {
                this.setOrderFields(response.data[0].status_row);
                await this.setLookupFields();
                this.usedResetStatus = true;
            });
        }
    }

    private voidStatus(ediStatus: ModelRow<any>) {
        if (ediStatus.get("direction") === "O") {
            const row = this.mainDataSource.activeRow;
            row.set("validate_model", false);
            row.set("ready_to_send", "V");
            this.statusdataheader.syncTitle();
        }
    }

    private restoreStatus(ediStatus: ModelRow<any>) {
        if (ediStatus.get("direction") === "O") {
            const row = this.mainDataSource.activeRow;
            row.set("validate_model", false);
            row.set("ready_to_send", "N");
            this.statusdataheader.syncTitle();
            this.saveButton.enabled = true;
        }
    }

    private async setLookupFields() {
        await this.setCommodityIdField();
        await this.setEquipmentTypeField();
        await this.setTrailerTypeField();
        await this.setDelayCodeField();
        await this.setTractorCodeField();
    }

    viewOrder() {
        const orderId = this.mainDataSource?.activeRow?.get("order_id", null);
        if (orderId != null)
            OrderTabView.navigateTo({ key: orderId, newTab: true });
    }

    doBeforeExecute(event: DataSourceExecutionEvent) {
        if (!this._createdPostListener && (event.getAction() === DataSourceAction.UPDATE)) {
            this.addBeforePostListeners();
        }

        if (ObjectUtil.isObject(event.filter) && Object.keys(event.filter).length == 0) {
            event.filter["empty_search"] = "*";
        }

        if(event.getAction() === DataSourceAction.ADD) {
            const row = this.mainDataSource.activeRow;
            if(row) {
                row.set("create_user_id", LMEUserSettings.getUserId());
                row.set("update_user_id", LMEUserSettings.getUserId());
                row.addBeforePostListener(() => this.lookupSalespersonFromCustomer(row));
            }
        }
    }

    addBeforePostListeners() {
        if (this.mainDataSource.activeRow != null) {
            const row = this.mainDataSource.activeRow;
            row.addBeforePostListener(async postEvent => {
                return new Promise(async (resolve) => {
                    this.saveBackToOrder = false;
                    this._disableSaveButton = false;
                    const origRow = this.mainDataSource.activeRow.originalData;
                    const origBatch: number = origRow["batch"];
                    const newBatch: number = parseInt(row.get("batch"));
                    const batchUpdated = origBatch !== newBatch;
                    row.set("from_view_edi_status", true);
                    if (((row.get("ready_to_send") !== "V" && row.get("batch") !== 0 && row.get("batch") !== null &&
                        row.get("batch") !== "" && row.get("batch") !== undefined && row.get("batch") !== "0") || batchUpdated) &&
                        row.get("direction") === "O") {
                        const dialogLayout = await this.getCorrectDialogLayout(row, this.usedResetStatus, this.isAppointmentStatus());
                        const usedOrderSwitch = dialogLayout instanceof RetransmitStatusModalOrderSave;

                        if (dialogLayout) {
                            await CommonDialogs.showYesNo(dialogLayout, "Retransmit Status", {
                                yesButtonCaption: "Retransmit",
                                noButtonCaption: "Cancel",
                                xVisible: false
                            }, false, 150).then(async clickedRetransmit => {
                                if (clickedRetransmit) {
                                    row.set("batch", 0);

                                    if (usedOrderSwitch) {
                                        const switchPanel = dialogLayout.layoutConfirmOrderSave as ConfirmOrderSave;
                                        this.saveBackToOrder = switchPanel.saveToOrderSwitch.checked;
                                        if (this.saveBackToOrder) {
                                            this.changedData = this.activeRow.getChangedData();
                                        }
                                    }

                                    if (this.saveButton.saveAction === SaveAction.SAVE) {
                                        this._disableSaveButton = true;
                                    }
                                } else {
                                    row.set("suppress_batch", true);
                                    this._disableSaveButton = true;
                                }

                                resolve(clickedRetransmit);
                            });
                        }
                    }
                    else if (((row.get("ready_to_send") === "V" || newBatch === 0)) && row.get("direction") === "O") {
                        if (!this.usedResetStatus) {
                            const orderUpdatesAvailable = await this.hasOrderUpdatesAvailable(this.isAppointmentStatus(), row);

                            if (orderUpdatesAvailable) {
                                await CommonDialogs.showYesNo("Would you like to save your changes back to the stop/order?").then(async clickedYes => {
                                    this.saveBackToOrder = clickedYes;
                                    if (this.saveBackToOrder) {
                                        this.changedData = this.activeRow.getChangedData();
                                    }
                                    this._disableSaveButton = true;
                                })
                            }
                            else {
                                this._disableSaveButton = true;
                            }
                        } else {
                            row.set("validate_model", false);
                        }

                        resolve(true);
                    }

                    this._createdPostListener = true;
                });
            })
        }
    }

    async getCorrectDialogLayout(currRow: ModelRow<any>, usedReset: boolean, isAppointment: boolean): Promise<Layout> {
        //Using reset eliminates option to save to order
        if (!usedReset) {
            const orderUpdatesAvailable = await this.hasOrderUpdatesAvailable(isAppointment, currRow);
            if (orderUpdatesAvailable) {
                return Layout.getLayout("lme/datafusion/RetransmitStatusModalOrderSave") as RetransmitStatusModalOrderSave;
            }
            else {
                return Layout.getLayout("lme/datafusion/RetransmitStatusModal") as RetransmitStatusModal;
            }
        }
        else {
            return Layout.getLayout("lme/datafusion/RetransmitStatusModal") as RetransmitStatusModal;
        }
    }

    async hasOrderUpdatesAvailable(isAppointment: boolean, currRow: ModelRow<any>): Promise<boolean> {
        const orderRowStatus = currRow.get("orders_status", "");
        if (orderRowStatus == "D" && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_ORDER)) {
            log.info("User denied permission to save changes to a delivered order.");
            return false;
        }

        const xferred2billing = currRow.get("orders_xferred2billing", "N");
        if (xferred2billing == "Y" && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_BILLED_ORDER)) {
            log.info("User denied permission to save changes to a billed order.");
            return false;
        }

        const xferToSettlement = currRow.get("movement_xfer2settle_date", null);
        if (xferToSettlement != null && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_MOVE_AFTER_SETTLED)) {
            log.info("User denied permission to save changes to a settled move.");
            return false;
        }

        const changedStatusData = this.activeRow.getChangedData();

        if (isAppointment) {
            const apptUpdated = await changedStatusData.hasOwnProperty("appointment_date") || changedStatusData.hasOwnProperty("delay_code");
            if (apptUpdated)
                return true;
        }

        if (changedStatusData.hasOwnProperty("delivery_signature") || changedStatusData.hasOwnProperty("blnum") ||
            changedStatusData.hasOwnProperty("curr_cases") || changedStatusData.hasOwnProperty("curr_weight") ||
            changedStatusData.hasOwnProperty("shipment_id") || changedStatusData.hasOwnProperty("refno") ||
            changedStatusData.hasOwnProperty("pro_nbr") || changedStatusData.hasOwnProperty("trip_lease") ||
            changedStatusData.hasOwnProperty("carrier_trailer") || changedStatusData.hasOwnProperty("equipment_type_id") ||
            changedStatusData.hasOwnProperty("commodity_id") || changedStatusData.hasOwnProperty("commodity") ||
            changedStatusData.hasOwnProperty("seal")) {
            return true;
        }

        return false;
    }

    async doAfterExecute(event: DataSourceExecutionEvent) {
        // If we're searching for a single record (has data and action is search), set the mode to UPDATE
        if (event.getAction() === DataSourceAction.SEARCH &&
            this.mainDataSource.mode === DataSourceMode.NONE &&
            this.mainDataSource.activeRow != null) {
            this.mainDataSource.mode = DataSourceMode.UPDATE;
        }

        if (event.getAction() === DataSourceAction.UPDATE && this.saveBackToOrder) {
            await this.saveUpdatesBackToOrder().catch((error) => {
                Snackbar.showWarningSnackbar("Error encountered saving values back to the stop/order." + error);
                this.saveDataToOrder = false;
            });
        }

        // Only initialize if we have an active row
        if (this.mainDataSource.activeRow != null) {
            this.initialize();
        }

        const row = this.mainDataSource.activeRow;
        if(row?.get("order_id") === undefined || row?.get("order_id") === null) {
            this.imageGoToOrder.visible = false;
            this.classicButton.visible = false;
            // this.labelOrderNumber.visible = false;
            // this.labelOrderId.visible = false;
        }

        const direction = row?.get("direction");
        if(direction === "O") {
            if (event.getAction() === DataSourceAction.UPDATE) {
                const row = this.mainDataSource.activeRow;

                // Store the validation promise so callers can await it
                this._postSaveValidationPromise = Api.post("lme/datafusion/validate-transaction", {
                    transaction_id: row?.get("id"),
                    transaction_type: "S"
                }).then(async () => {
                    await this.mainDataSource.search(this.mainDataSource.lastSearch);
                }).catch((error) => {
                    console.log("Error validating transaction after status update: ", error);
                });
                await this._postSaveValidationPromise;
            }
        }
    }

    async saveUpdatesBackToOrder(): Promise<any> {
        const row = this.mainDataSource.activeRow;
        const changedData = this.changedData;

        const stopId = row.get("curr_stop_id");
        const statusId = row.get("id");
        const newApptDate = changedData.hasOwnProperty("appointment_date") ? row.get("appointment_date") : null;
        const newDelayCode = changedData.hasOwnProperty("delay_code") ? row.get("delay_code") : null;
        const newSignedForName = changedData.hasOwnProperty("delivery_signature") ? row.get("delivery_signature") : null;
        const newCurrCases = changedData.hasOwnProperty("curr_cases") ? row.get("curr_cases") : null;
        const newCurrWeight = changedData.hasOwnProperty("curr_weight") ? row.get("curr_weight") : null;
        const newBlnum = changedData.hasOwnProperty("blnum") ? row.get("blnum") : null;
        const newShipId = changedData.hasOwnProperty("shipment_id") ? row.get("shipment_id") : null;
        const newRefno = changedData.hasOwnProperty("refno") ? row.get("refno") : null;
        const newProNbr = changedData.hasOwnProperty("pro_nbr") ? row.get("pro_nbr") : null;
        const newTripLease = changedData.hasOwnProperty("trip_lease") ? row.get("trip_lease") : null;
        const newCarrierTrailer = changedData.hasOwnProperty("carrier_trailer") ? row.get("carrier_trailer") : null;
        const newTrailerType = changedData.hasOwnProperty("equipment_type_id") ? row.get("equipment_type_id") : null;
        const newCommodityId = changedData.hasOwnProperty("commodity_id") ? row.get("commodity_id") : null;
        const newCommodity = changedData.hasOwnProperty("commodity") ? row.get("commodity") : null;
        const newSeal = changedData.hasOwnProperty("seal") ? row.get("seal") : null;

        this.saveDataToOrder = true;
        return Api.search("lme/datafusion/update-order-from-status", {
            status_id: statusId,
            stop_id: stopId,
            appt_date: newApptDate,
            delay_code: newDelayCode,
            signed_for_name: newSignedForName,
            curr_cases: newCurrCases,
            curr_weight: newCurrWeight,
            blnum: newBlnum,
            ship_id: newShipId,
            cons_refno: newRefno,
            pro_nbr: newProNbr,
            trip_lease: newTripLease,
            carrier_trailer: newCarrierTrailer,
            trailer_type: newTrailerType,
            commodity_id: newCommodityId,
            commodity: newCommodity,
            seal: newSeal
        }).catch((error) => {
            Snackbar.showWarningSnackbar("Error encountered saving values back to the stop/order." + error);
            this.saveDataToOrder = false;
        });
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.edi.EntryEdiStatus", this.mainDataSource.activeRow.get("id"));
    }

    public get doAfterAction(): () => void {
        return this._doAfterAction;
    }

    public set doAfterAction(value: () => void) {
        this._doAfterAction = value;
    }

    private executeDoAfterAction() {
        if (this.doAfterAction != null)
            this.doAfterAction();
    }

    public get viewOrderURL() {
        return this._viewOrderURL;
    }

    public set viewOrderURL(value) {
        this._viewOrderURL = value;
    }

    private setOrderStatusLabel(status: string): string {
        switch (status) {
            case "A":
                return "Available";
            case "D":
                return "Delivered";
            case "P":
                return "In Progress";
            case "Q":
                return "Quote";
            case "V":
                return "Void";
        }
    }

    private setOrderFields(statusRow: any) {
        for (const item of this.fieldList) {
            if (item === "ordered_date" || item === "sched_arrive_early" || item === "actual_arrival" || item === "actual_departure" ||
                item === "appointment_date" || item === "status_date" || item === "sched_arrive_early" || item === "sched_arrive_late") {
                if (statusRow[item] !== null && statusRow[item] !== "" && statusRow[item] !== undefined) {
                    const orderedDate = DateUtil.formatDateTime(DateUtil.parseDateTime(statusRow[item]), "yyyy-MM-dd HH:mm:ss");
                    this.mainDataSource.activeRow.set(item, orderedDate);
                }
            } else {
                this.mainDataSource.activeRow.set(item, statusRow[item]);
            }
        }
    }

    private async setCommodityIdField() {
        if (this.mainDataSource.activeRow.get("commodity_id") !== null &&
            this.mainDataSource.activeRow.get("commodity_id") !== "" &&
            this.mainDataSource.activeRow.get("commodity_id") !== undefined) {
            const commodityId = this.mainDataSource.activeRow.get("commodity_id");
            const response = await Model.searchSingleRecord("lme/dispatch/commodity", { id: commodityId });
            if (response != null) {
                const lmModelRow = new ModelRow(this.layoutEdiStatusOrderTab.textboxCommodityId.lookupModel, false, {
                    id: response.get("id"),
                    descr: response.get("descr")
                });
                lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                this.layoutEdiStatusOrderTab.textboxCommodityId.dataSource.activeRow.setLookupModelData("commodity_id", lmModelRow);
                this.layoutEdiStatusOrderTab.textboxCommodityId.dataSource.activeRow.set("commodity_id", response.get("id"));
                this.layoutEdiStatusOrderTab.textboxCommodityId.displayData(this.layoutEdiStatusOrderTab.textboxCommodityId.dataSource.activeRow, null, 0);
            }

        }
    }

    private async setEquipmentTypeField() {
        if (this.mainDataSource.activeRow.get("equipment_type_id") !== null &&
            this.mainDataSource.activeRow.get("equipment_type_id") !== "" &&
            this.mainDataSource.activeRow.get("equipment_type_id") !== undefined) {
            const equipmentTypeId = this.mainDataSource.activeRow.get("equipment_type_id");
            const response = await Model.searchSingleRecord("lme/dispatch/equipment-type", { id: equipmentTypeId }, "lme/dispatch/EquipmentTypeMaster");
            if (response != null) {
                const lmModelRow = new ModelRow(this.layoutEdiStatusOrderTab.textboxTrailerType.lookupModel, false, {
                    id: response.get("id"),
                    descr: response.get("descr")
                });
                lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                this.layoutEdiStatusOrderTab.textboxTrailerType.dataSource.activeRow.setLookupModelData("equipment_type_id", lmModelRow);
                this.layoutEdiStatusOrderTab.textboxTrailerType.dataSource.activeRow.set("equipment_type_id", response.get("id"));
                this.layoutEdiStatusOrderTab.textboxTrailerType.displayData(this.layoutEdiStatusOrderTab.textboxTrailerType.dataSource.activeRow, null, 0);
            }

        }
    }

    private async setTrailerTypeField() {
        if (this.mainDataSource.activeRow.get("trailer_id") !== null &&
            this.mainDataSource.activeRow.get("trailer_id") !== "" &&
            this.mainDataSource.activeRow.get("trailer_id") !== undefined) {
            const equipmentTypeId = this.mainDataSource.activeRow.get("trailer_id");
            const response = await Model.searchSingleRecord("lme/dispatch/asset/trailer-lookup", { id: equipmentTypeId }, "lme/dispatch/asset/TrailerLookup");
            if (response != null) {
                const lmModelRow = new ModelRow(this.layoutEdiStatusOrderTab.textboxTrailerId.lookupModel, false, {
                    id: response.get("id"),
                    descr: response.get("descr")
                });
                lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                this.layoutEdiStatusOrderTab.textboxTrailerId.dataSource.activeRow.setLookupModelData("trailer_id", lmModelRow);
                this.layoutEdiStatusOrderTab.textboxTrailerId.dataSource.activeRow.set("trailer_id", response.get("id"));
                this.layoutEdiStatusOrderTab.textboxTrailerId.displayData(this.layoutEdiStatusOrderTab.textboxTrailerId.dataSource.activeRow, null, 0);
            }

        }
    }


    private async setDelayCodeField() {
        if (this.mainDataSource.activeRow.get("delay_code") !== null &&
            this.mainDataSource.activeRow.get("delay_code") !== "" &&
            this.mainDataSource.activeRow.get("delay_code") !== undefined) {
            const delayCode = this.mainDataSource.activeRow.get("delay_code");
            const response = await Model.searchSingleRecord("lme/datafusion/delay-code", { id: delayCode }, "lme/datafusion/EdiDelayCode");
            if (response != null) {
                const lmModelRow = new ModelRow(this.layoutEdiStatusDetailTabLayout.textboxDelayCode.lookupModel, false, {
                    id: response.get("id"),
                    descr: response.get("descr")
                });
                lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                this.layoutEdiStatusDetailTabLayout.textboxDelayCode.dataSource.activeRow.setLookupModelData("delay_code", lmModelRow);
                this.layoutEdiStatusDetailTabLayout.textboxDelayCode.dataSource.activeRow.set("delay_code", response.get("id"));
                this.layoutEdiStatusDetailTabLayout.textboxDelayCode.displayData(this.layoutEdiStatusDetailTabLayout.textboxDelayCode.dataSource.activeRow, null, 0);
            }

        }
    }

    private async lookupSalespersonFromCustomer(row: ModelRow<any>): Promise<void> {
        const customerId = row.get("customer_id");
        if (customerId !== null && customerId !== "" && customerId !== undefined) {
            const customerRow = await Model.searchSingleRecord("lme/ar/customer", { id: customerId });
            if (customerRow != null) {
                const salespersonId = customerRow.get("salesperson_id");
                if (salespersonId !== null && salespersonId !== "" && salespersonId !== undefined) {
                    row.set("salesperson_id", salespersonId);
                }
            }
        }
    }

    private async setTractorCodeField() {
        if (this.mainDataSource.activeRow.get("tractor_id") !== null &&
            this.mainDataSource.activeRow.get("tractor_id") !== "" &&
            this.mainDataSource.activeRow.get("tractor_id") !== undefined) {
            const tractorCode = this.mainDataSource.activeRow.get("tractor_id");
            const response = await Model.searchSingleRecord("lme/dispatch/asset/tractor", { id: tractorCode }, "lme/dispatch/asset/TractorLookup");
            if (response != null) {
                const lmModelRow = new ModelRow(this.layoutEdiStatusOrderTab.textboxTractorId.lookupModel, false, {
                    id: response.get("id"),
                    descr: response.get("descr")
                });
                lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                this.layoutEdiStatusOrderTab.textboxTractorId.dataSource.activeRow.setLookupModelData("tractor_id", lmModelRow);
                this.layoutEdiStatusOrderTab.textboxTractorId.dataSource.activeRow.set("tractor_id", response.get("id"));
                this.layoutEdiStatusOrderTab.textboxTractorId.displayData(this.layoutEdiStatusOrderTab.textboxTractorId.dataSource.activeRow, null, 0);
            }
        }
    }

    public get saveButton(): SaveButton {
        return this.statusdataheader?.["saveButton"];
    }

    isAppointmentStatus(): boolean {
        const row = this.mainDataSource.activeRow;
        return row.get("event_type") == "AP" ? true : false;
    }

    setDirectionSpecificPermissions() {
        if (this.mainDataSource.activeRow != null) {
            const row = this.mainDataSource.activeRow;

            if (row.get("direction") === "I") {
                this.dataHeader.showExecute = false;
                this.layoutEdiStatusOrderTab.textboxShipmentId.enabled = false;
                this.layoutEdiStatusOrderTab.textboxBlnum.enabled = false;
                this.layoutEdiStatusOrderTab.textboxRefno.enabled = false;
                this.layoutEdiStatusOrderTab.textboxProNbr.enabled = false;
                this.layoutEdiStatusOrderTab.textboxTripLease.enabled = false;
                this.layoutEdiStatusOrderTab.textboxCarrierTrailer.enabled = false;
                this.layoutEdiStatusOrderTab.textboxTrailerType.enabled = false;
                this.layoutEdiStatusOrderTab.textboxCommodityId.enabled = false;
                this.layoutEdiStatusOrderTab.textboxCommodity.enabled = false;
                this.layoutEdiStatusOrderTab.textboxDeliverySignature.enabled = false;
                this.layoutEdiStatusOrderTab.textboxCurrCases.enabled = false;
                this.layoutEdiStatusOrderTab.textboxCurrWeight.enabled = false;
                this.layoutEdiStatusOrderTab.textboxSeal.enabled = false;
                this.saveButton.enabled = false;
                this.tabComments.visible = true;
            }
            else {
                this.layoutEdiStatusOrderTab.textboxShipmentId.enabled = true;
                this.layoutEdiStatusOrderTab.textboxBlnum.enabled = true;
                this.layoutEdiStatusOrderTab.textboxRefno.enabled = true;
                this.layoutEdiStatusOrderTab.textboxProNbr.enabled = true;
                this.layoutEdiStatusOrderTab.textboxTripLease.enabled = true;
                this.layoutEdiStatusOrderTab.textboxCarrierTrailer.enabled = true;
                this.layoutEdiStatusOrderTab.textboxTrailerType.enabled = true;
                this.layoutEdiStatusOrderTab.textboxCommodityId.enabled = true;
                this.layoutEdiStatusOrderTab.textboxCommodity.enabled = true;
                this.layoutEdiStatusOrderTab.textboxDeliverySignature.enabled = true;
                this.layoutEdiStatusOrderTab.textboxCurrCases.enabled = true;
                this.layoutEdiStatusOrderTab.textboxCurrWeight.enabled = true;
                this.layoutEdiStatusOrderTab.textboxSeal.enabled = true;
                this.saveButton.enabled = true;
                this.tabComments.visible = false;
            }

        }
    }

    setSearchSpecificProperties() {
    if (this.sourceEdistatus.activeRow) {
            const orderId = this.sourceEdistatus.activeRow.get("order_id");

            if (orderId) {
                this.imageGoToOrder.visible = true;
                this.classicButton.visible = true;
            } else {
                this.imageGoToOrder.visible = false;
                this.classicButton.visible = false;
            }
        } else {
            this.imageGoToOrder.visible = false;
            this.classicButton.visible = false;
        }

    }

    public get usedResetStatus() {
        return this._usedResetStatus;
    }

    public set usedResetStatus(value) {
        this._usedResetStatus = value;
    }

    openRawDataSlideout(rawData: boolean, buttonClicked?: string) {
        const shipmentStatusId = this.mainDataSource?.activeRow?.get("id");
        const statusCode = this.mainDataSource?.activeRow?.get("status_code");
        const stopDescription = this.mainDataSource?.activeRow?.get("stop_description");
        const templateType = this.mainDataSource?.activeRow?.get("edi_template_format_type");
        EdiRawDataSlideout.openSlideout({title: `Shipment Status - ${statusCode}/${stopDescription}`, shipmentStatusId, templateType, rawData, buttonClicked});
    }

    rawDataOnClick(event: ClickEvent) {
        const buttonId: string = event.target.id;
        this.openRawDataSlideout(true, buttonId);
    }

    public override get crudDecoratorProps(): Partial<LayoutProvidedCrudDecoratorProps> {
        return { customDataLoader: this.loadFromApiData.bind(this) };
    }

    public loadFromApiData = (apiData: any, mode: DataSourceMode) => {
        const status = new ModelRow("lme/datafusion/edistatus", true, apiData);
        this.mainDataSource.setRowsAndMode(mode, [status]);
        this.mainDataSource.activeRow.set("edistatus_profile.partner_name", apiData.partner_name);

        // Initialize the layout after data is loaded
        setTimeout(() => {
            this.initialize();
            this.layoutGeneral.initialize(this.mainDataSource.activeRow);
            this.layoutEdiStatusDetailTabLayout.initialize(this.mainDataSource.activeRow, true);
            this.buttonStatusRawData.enabled = false;
        }, 0);
    }

    private initialize() {
        // Don't initialize if there's no active row (e.g., after deletion)
        if (this.mainDataSource.activeRow == null) {
            return;
        }

        const direction = this.mainDataSource.activeRow?.get("direction");
        if (this.sourceEdistatus.activeRow) {
            const orderStatus = this.sourceEdistatus.activeRow.get("orders_status");
            const orderId = this.sourceEdistatus.activeRow.get("order_id");

            if (orderStatus) {
                this.labelOrderStatus.text = this.setOrderStatusLabel(orderStatus);
                this.labelOrderStatus.visible = true;
            } else {
                this.labelOrderStatus.visible = false;
            }
            if (orderId) {
                this.imageGoToOrder.visible = true;
                this.classicButton.visible = true;
                this.layoutEdiStatusDetailTabLayout.textboxEta.displayType = DisplayType.DATETIME;
                this.layoutEdiStatusDetailTabLayout.textboxAppointmentDate.displayType = DisplayType.DATETIME;
                this.layoutEdiStatusDetailTabLayout.textboxStatusDate.displayType = DisplayType.DATETIME;
            } else {
                this.imageGoToOrder.visible = false;
                this.classicButton.visible = false;
                this.layoutEdiStatusDetailTabLayout.textboxEta.displayType = DisplayType.DATERANGE;
                this.layoutEdiStatusDetailTabLayout.textboxAppointmentDate.displayType = DisplayType.DATERANGE;
                this.layoutEdiStatusDetailTabLayout.textboxStatusDate.displayType = DisplayType.DATERANGE;
            }
        } else {
            this.labelOrderStatus.visible = false;
            this.imageGoToOrder.visible = false;
            this.classicButton.visible = false;
            this.layoutEdiStatusDetailTabLayout.textboxEta.displayType = DisplayType.DATETIME;
            this.layoutEdiStatusDetailTabLayout.textboxAppointmentDate.displayType = DisplayType.DATETIME;
            this.layoutEdiStatusDetailTabLayout.textboxStatusDate.displayType = DisplayType.DATETIME;
        }
        if (this.mainDataSource.activeRow?.get("partner_name") === "Profile Not Found") {
            Snackbar.showWarningSnackbar("Partner profile has been deleted.");
        }
        this.statusdataheader.syncTitle();
        if (this.mainDataSource.activeRow?.get("suppress_batch")) {
            this.mainDataSource.activeRow.set("suppress_batch", false);
            this.mainDataSource.activeRow.resetOriginalData("suppress_batch");
        }
        const funcAckId = this.mainDataSource.activeRow?.get("func_ack_id");
        if ("I" == direction) {
            const sendFuncAck = this.mainDataSource.activeRow?.get("edistatus_profile_send_func_ack");
            if (sendFuncAck != null) {
                this.tabFuncAck.visible = sendFuncAck == "Y";
                this.buttonFuncAckRawData.enabled = funcAckId !== undefined && funcAckId !== null;
            }
        }
        else {
            this.tabFuncAck.visible = true;
            this.buttonFuncAckRawData.enabled = funcAckId !== undefined && funcAckId !== null;
        }
        if (this._disableSaveButton) this.saveButton.enabled = false;
        this.setDirectionSpecificPermissions();

        // If the mode is still NONE but we have data, force it to UPDATE mode for proper delete button display
        let effectiveMode = this.mainDataSource.mode;
        if (effectiveMode === DataSourceMode.NONE && this.mainDataSource.activeRow != null) {
            effectiveMode = DataSourceMode.UPDATE;
        }

        // Force a refresh of the header tools - this will re-evaluate the delete button
        log.debug("=== INITIALIZE - SYNCING TITLE ===");
        log.debug("Current data source mode:", this.mainDataSource.mode);
        log.debug("Effective mode:", effectiveMode);
        log.debug("StatusDataHeader showDelete:", this.statusdataheader.showDelete);
        log.debug("Active row direction:", this.mainDataSource.activeRow?.get("direction"));
        log.debug("Active row transmitted:", this.mainDataSource.activeRow?.get("transmitted"));

        this.statusdataheader.syncTitle(effectiveMode);

        log.debug("=== END INITIALIZE - SYNCING TITLE ===");
    }


    public get saveBackToOrder(): boolean {
        return this._saveBackToOrder;
    }


    public set saveBackToOrder(v: boolean) {
        this._saveBackToOrder = v;
    }

    public get changedData(): Partial<any> {
        return this._changedData;
    }

    public set changedData(data: Partial<any>) {
        this._changedData = data;
    }


    public get saveDataToOrder(): boolean {
        return this._saveDataToOrder;
    }

    public set saveDataToOrder(v: boolean) {
        this._saveDataToOrder = v;
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        log.debug("=== doAfterDataHeaderSetup called ===");

        // Explicitly enable the delete button
        dataHeader.showDelete = true;
        log.debug("Set showDelete to true in doAfterDataHeaderSetup");

        // Debug the conditions for delete button visibility
        log.debug("DataHeader conditions check:");
        log.debug("  displayingInViewMode:", this.displayingInViewMode);
        log.debug("  dataSource.mode:", dataHeader.dataSource?.mode);
        log.debug("  showDelete:", dataHeader.showDelete);

        // Create the custom delete function
        const customDeleteFunction = (row: ModelRow) => {
            if (!row) {
                log.debug("No row provided, returning false");
                return false;
            }

            const direction = row.get("direction");
            const transmitted = row.get("transmitted");

            log.debug("Direction:", direction, "Transmitted:", transmitted);

            // Inbound records (direction === "I") can always be deleted
            if (direction === "I") {
                return true;
            }

            // Outbound records can only be deleted if not transmitted
            const canDelete = transmitted !== "Yes";
            return canDelete;
        };

        // Override the allowRowDelete property with a getter that always returns our function
        Object.defineProperty(dataHeader, 'allowRowDelete', {
            get: () => {
                log.debug("allowRowDelete getter called - returning custom function");
                return customDeleteFunction;
            },
            set: (value) => {
                log.debug("allowRowDelete setter called - ignoring attempt to set:", value);
                // Ignore attempts to set this property
            },
            configurable: true,
            enumerable: true
        });

        // Store reference to the original syncTitle method
        const originalSyncTitle = dataHeader.syncTitle.bind(dataHeader);

        // Override syncTitle to ensure delete button stays in panel
        dataHeader.syncTitle = function(mode?: any) {
            log.debug("Custom syncTitle called with mode:", mode);

            // Call the original syncTitle method
            const result = originalSyncTitle(mode);

            // After syncTitle runs, ensure the delete button is in the panel
            const deleteButton = (this as any).deleteButton;
            const panelTools = (this as any).panelTools;

            if (deleteButton && panelTools) {
                const panelComponents = (panelTools as any)._components;
                if (panelComponents && Array.isArray(panelComponents)) {
                    const buttonExists = panelComponents.some((comp: any) => comp === deleteButton);
                    if (!buttonExists) {
                        log.debug("Re-adding delete button to panel after syncTitle");
                        panelComponents.push(deleteButton);
                        deleteButton.visible = true;
                        deleteButton.enabled = true;
                    }

                    // Force the delete button into the correct DOM position
                    const deleteButtonElement = (deleteButton as any).element;
                    const panelElement = (panelTools as any)._element;

                    if (deleteButtonElement && panelElement) {
                        const panelRow = panelElement.querySelector('.pnl-panelRow');
                        if (panelRow) {
                            // Check if delete button is already in the panel row
                            const deleteButtonInRow = panelRow.querySelector('#statusdataheader-deleteButton');
                            if (!deleteButtonInRow) {
                                log.debug("Adding delete button to panel row DOM");

                                // Remove the delete button from its current parent (if any)
                                if (deleteButtonElement.parentNode) {
                                    deleteButtonElement.parentNode.removeChild(deleteButtonElement);
                                }

                                // Find the close button to insert delete button before it
                                const closeButton = panelRow.querySelector('#statusdataheader-closeButton');
                                if (closeButton) {
                                    log.debug("Inserting delete button before close button");
                                    panelRow.insertBefore(deleteButtonElement, closeButton);
                                } else {
                                    log.debug("Close button not found, appending delete button to panel row");
                                    panelRow.appendChild(deleteButtonElement);
                                }

                                // Force visibility
                                deleteButtonElement.style.display = 'block';
                                deleteButtonElement.style.visibility = 'visible';
                                deleteButtonElement.style.opacity = '1';
                                log.debug("Delete button added to panel row with proper positioning");
                            }
                        }
                    }

                    // Force refresh of the panel to ensure DOM is updated
                    if ((panelTools as any).refresh) {
                        (panelTools as any).refresh();
                        log.debug("Forced panel refresh after syncTitle");
                    }

                    if ((panelTools as any).render) {
                        (panelTools as any).render();
                        log.debug("Forced panel render after syncTitle");
                    }

                    if ((panelTools as any).invalidate) {
                        (panelTools as any).invalidate();
                        log.debug("Forced panel invalidate after syncTitle");
                    }

                    // Also try to force re-render of the delete button itself
                    if ((deleteButton as any).refresh) {
                        (deleteButton as any).refresh();
                        log.debug("Forced delete button refresh");
                    }

                    if ((deleteButton as any).render) {
                        (deleteButton as any).render();
                        log.debug("Forced delete button render");
                    }
                }
            }

            return result;
        };

        log.debug("Set up allowRowDelete property override and syncTitle override in doAfterDataHeaderSetup");
        log.debug("=== END doAfterDataHeaderSetup ===");
    }

    // Debug method to check data header state - can be called from browser console
    debugDataHeader() {
        console.log("=== DATA HEADER DEBUG ===");
        console.log("StatusDataHeader object:", this.statusdataheader);
        console.log("StatusDataHeader showDelete:", this.statusdataheader.showDelete);
        console.log("StatusDataHeader allowRowDelete function:", this.statusdataheader.allowRowDelete);
        console.log("StatusDataHeader displayingInViewMode:", this.statusdataheader.layout?.displayingInViewMode);
        console.log("StatusDataHeader dataSource mode:", this.statusdataheader.dataSource?.mode);
        console.log("StatusDataHeader panelTools:", this.statusdataheader.panelTools);
        console.log("Active row:", this.mainDataSource.activeRow);
        console.log("Active row direction:", this.mainDataSource.activeRow?.get("direction"));
        console.log("Active row transmitted:", this.mainDataSource.activeRow?.get("transmitted"));

        // Test the allowRowDelete function
        if (this.statusdataheader.allowRowDelete && this.mainDataSource.activeRow) {
            const result = this.statusdataheader.allowRowDelete(this.mainDataSource.activeRow);
            console.log("allowRowDelete result:", result);
        } else {
            console.log("No allowRowDelete function or no active row");
        }

        // Check property descriptor for allowRowDelete
        const propertyDescriptor = Object.getOwnPropertyDescriptor(this.statusdataheader, 'allowRowDelete');
        console.log("allowRowDelete property descriptor:", propertyDescriptor);

        // Check if there's a delete button by ID
        try {
            const deleteButton = (this.statusdataheader as any).deleteButton;
            console.log("Delete button found:", deleteButton);
            console.log("Delete button visible:", deleteButton?.visible);
            console.log("Delete button enabled:", deleteButton?.enabled);

            // Get the parent panel to check its contents
            const panelTools = this.statusdataheader.panelTools;
            console.log("Panel tools components:", (panelTools as any)._components);

            // Try to find all buttons in the panel by looking at _components
            if ((panelTools as any)._components) {
                const components = (panelTools as any)._components;
                components.forEach((child: any, index: number) => {
                    console.log(`Panel component ${index}:`, child);
                    console.log(`  Type: ${child.constructor.name}`);
                    console.log(`  ID: ${child.id}`);
                    console.log(`  Visible: ${child.visible}`);
                    console.log(`  Enabled: ${child.enabled}`);
                    if (child.constructor.name === 'Button') {
                        console.log(`  Button text: ${child.text}`);
                    }
                });
            }

            // Also check if the delete button is in the DOM
            const deleteButtonElement = document.getElementById('deleteButton');
            console.log("Delete button DOM element:", deleteButtonElement);
            console.log("Delete button DOM visible:", deleteButtonElement?.style.display);

            // Try alternative DOM selectors
            const deleteButtonByDataId = document.querySelector('[data-id="statusdataheader-deleteButton"]');
            console.log("Delete button by data-id:", deleteButtonByDataId);

            const deleteButtonByClassName = document.querySelector('.deleteButton');
            console.log("Delete button by class name:", deleteButtonByClassName);

            // Look for any button with delete in the id or class
            const allButtons = document.querySelectorAll('button');
            console.log("All buttons on page:", allButtons.length);

            const deleteButtons = Array.from(allButtons).filter(btn =>
                btn.id.includes('delete') ||
                btn.className.includes('delete') ||
                btn.getAttribute('data-id')?.includes('delete')
            );
            console.log("Buttons with 'delete' in id/class/data-id:", deleteButtons);

            // Check the actual DOM element of the delete button component
            if (deleteButton && (deleteButton as any).element) {
                console.log("Delete button component DOM element:", (deleteButton as any).element);
                console.log("Delete button DOM element display:", (deleteButton as any).element?.style.display);
            }

            // Check if the panelTools element is in the DOM and visible
            const panelElement = (panelTools as any)._element;
            console.log("Panel tools DOM element:", panelElement);
            console.log("Panel tools DOM visible:", panelElement?.style.display);

            if (panelElement) {
                console.log("Panel tools children in DOM:", panelElement.children);

                // Check the panel row structure
                const panelRow = panelElement.querySelector('.pnl-panelRow');
                if (panelRow) {
                    console.log("Panel row found:", panelRow);
                    console.log("Panel row children:", panelRow.children);
                    console.log("Panel row style:", panelRow.style.cssText);
                    console.log("Panel row computed style display:", getComputedStyle(panelRow).display);
                    console.log("Panel row computed style visibility:", getComputedStyle(panelRow).visibility);

                    // Check if the delete button is inside the panel row
                    const deleteButtonInRow = panelRow.querySelector('#statusdataheader-deleteButton');
                    console.log("Delete button in panel row:", deleteButtonInRow);

                    if (deleteButtonInRow) {
                        console.log("Delete button computed style display:", getComputedStyle(deleteButtonInRow).display);
                        console.log("Delete button computed style visibility:", getComputedStyle(deleteButtonInRow).visibility);
                        console.log("Delete button computed style opacity:", getComputedStyle(deleteButtonInRow).opacity);
                        console.log("Delete button computed style width:", getComputedStyle(deleteButtonInRow).width);
                        console.log("Delete button computed style height:", getComputedStyle(deleteButtonInRow).height);
                    } else {
                        console.log("Delete button NOT found in panel row - this is the problem!");
                    }
                } else {
                    console.log("Panel row NOT found - this could be the issue!");
                }
            }
        } catch (e) {
            console.log("Could not access delete button:", e);
        }

        console.log("=== END DATA HEADER DEBUG ===");
        return this.statusdataheader;
    }

    // Force the DataHeader to rebuild its tools
    forceRebuildDataHeaderTools() {
        console.log("=== FORCE REBUILD DATA HEADER TOOLS ===");

        try {
            const dataHeader = this.statusdataheader;
            console.log("DataHeader before rebuild:", dataHeader);

            // Try to call any internal methods that might rebuild the tools
            if ((dataHeader as any).buildTools) {
                (dataHeader as any).buildTools();
                console.log("Called dataHeader.buildTools()");
            }

            if ((dataHeader as any).rebuildTools) {
                (dataHeader as any).rebuildTools();
                console.log("Called dataHeader.rebuildTools()");
            }

            if ((dataHeader as any).setupTools) {
                (dataHeader as any).setupTools();
                console.log("Called dataHeader.setupTools()");
            }

            if ((dataHeader as any).buildButtons) {
                (dataHeader as any).buildButtons();
                console.log("Called dataHeader.buildButtons()");
            }

            if ((dataHeader as any).buildDeleteButton) {
                (dataHeader as any).buildDeleteButton();
                console.log("Called dataHeader.buildDeleteButton()");
            }

            // Force a complete refresh
            dataHeader.syncTitle();
            console.log("Called dataHeader.syncTitle()");

            // Check the components after rebuild
            const panelTools = dataHeader.panelTools;
            const panelComponents = (panelTools as any)._components;
            console.log("Panel components after rebuild:", panelComponents);

        } catch (e) {
            console.log("Error in forceRebuildDataHeaderTools:", e);
        }

        console.log("=== END FORCE REBUILD DATA HEADER TOOLS ===");
    }
}
